import { useContext, useEffect, useMemo, useState } from "react";
import { formJson } from "../../../utils/formJson";
import _ from "underscore";
import services from "../../../services/services";
import { useCommanKit } from "./useCommanKit";
import { convertToBase64, dateConvert, toLocalDateString } from "../../../utils/Helper";
import { Urls } from "../../../services/Urls";
import { ToastContext } from "../../../context/ToastContext";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export const useFormTable = ({ data, label, fieldsDetails, handleAPI, row, familyList }) => {
    const { showSuccess, showError, showInfo } = useContext(ToastContext);
    const fieldData = fieldsDetails || formJson?.[label]?.fieldData;
    const { getDataKey } = formJson?.[label]
    const { getEmployeeDetails } = useCommanKit({ data });
    const [familyData, setFamilyData] = useState(null);
    const [updatedFieldData, setUpdatedFieldData] = useState({});
    const [showPopup, setShowPopup] = useState({
        visibel: false,
        section: "",
        idx: "",
        row: ""
    })

    const getInitialFormValues = () =>
        fieldData
            ? Object.fromEntries(
                Object.entries(fieldData).map(([section, fields]) => [
                    section,
                    Object.fromEntries(fields.inputs?.map(f => [f.name, ""]))
                ])
            )
            : {};
    const getInitialRows = (existingRows = {}) =>
        fieldData
            ? Object.fromEntries(
                Object.keys(fieldData).map(section => [
                    section,
                    existingRows[section]?.length ? existingRows[section] : [],
                ])
            )
            : {};

    const [editIndex, setEditIndex] = useState({});
    const [formValues, setFormValues] = useState(getInitialFormValues());
    const [rows, setRows] = useState(getInitialRows());
    const [formErrors, setFormErrors] = useState({});

    // reset the state when fieldData change or save/edit/delete
    useEffect(() => {
        setFormValues(getInitialFormValues());
        setRows(prevRows => getInitialRows(prevRows));
        setEditIndex({});
    }, [fieldData]);

    // Handlers per section
    const handleChange = async (section, e) => {
        const { name, value } = e.target;
        const file = e.target?.files?.[0]
        if (file) {
            const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'image/webp'];
            if (!allowedTypes.includes(file.type)) {
                showInfo("warn", "Only image and PDF files are allowed.");
                return;
            }
            if (file.size > 20 * 1024 * 1024) {
                showInfo("warn", "File size must be less than 20MB.");
                return;
            }
            const base64 = await convertToBase64(file)
            const url = URL.createObjectURL(file);
            setFormValues(prev => ({
                ...prev,
                [section]: {
                    ...prev[section], [name]: url,
                    [`${name}_id`]: base64?.split(",")?.[1]
                }
            }));
        } else {
            setFormValues(prev => ({
                ...prev,
                [section]: { ...prev[section], [name]: value }
            }));
        }
    };

    const validateForm = (fields, formValues) => {
        const errors = {};

        fields?.inputs?.forEach(field => {
            if (field.depand && !formValues[field.depand]) return;
            if (field.type === "button") return;
            if (field.required) {
                if (!formValues[field.name] || formValues[field.name].toString().trim() === "") {
                    errors[field.name] = `${field.label} is required`;
                }
            }
            if (field.type === "number" && formValues[field.name]) {
                if (isNaN(formValues[field.name])) {
                    errors[field.name] = `${field.label} must be a number`;
                }
            }
            if (field.name?.toLowerCase().includes("contact") || field.name?.toLowerCase().includes("mobile")) {
                if (formValues[field.name] && !/^\d{10}$/.test(formValues[field.name])) {
                    errors[field.name] = `${field.label} must be a valid 10-digit number`;
                }
            }
        });

        return errors;
    };

    function buildPayloadBySchema(schema, formValues) {
        const payload = {};

        for (const key in schema) {
            if (Object.hasOwn(formValues, key)) {
                let val = formValues[key];
                const schemaVal = schema[key];
                if (typeof schemaVal === "number") {
                    if (val instanceof Date) {
                        payload[key] = val.getFullYear();
                    } else {
                        payload[key] = Number(val) || 0;
                    }
                }
                else if (typeof schemaVal === "string") {
                    if (val instanceof Date) {
                        payload[key] = toLocalDateString(val);
                    } else {
                        payload[key] = val != null ? String(val) : "";
                    }
                }
                else {
                    payload[key] = val;
                }
            } else {
                payload[key] = schema[key];
            }
        }
        return payload;
    }

    // Add this helper function before handleSave
    const checkDuplicateEntry = (section, formValues) => {
        const existingRows = rows[section] || [];
        const currentEditIndex = editIndex[section];
        const uniqueFields =  ['language_item_id']
        for (const fieldName of uniqueFields) {
            if (formValues[fieldName]) {
                const newValue = formValues[fieldName].toString().trim().toLowerCase();
                const duplicateExists = existingRows.some((row, idx) => {
                    if (idx === currentEditIndex) return false;
                    return row[fieldName]?.toString().trim().toLowerCase() === newValue;
                });

                if (duplicateExists) {
                    return { isDuplicate: true, field: fieldName, message: `${fieldData[section].inputs.find(f => f.name === fieldName)?.label || fieldName} already exists` };
                }
            }
        }
        return { isDuplicate: false };
    };

    // save the data based on sections
    const handleSave = (section, e) => {
        e.preventDefault();
        let allErrors = {};
        const duplicateCheck = checkDuplicateEntry(section, formValues[section] || {});
        if (duplicateCheck.isDuplicate) {
            showInfo("Info", duplicateCheck.message);
            return;
        }
        const sectionErrors = validateForm(fieldData[section], formValues[section] || {});
        if (Object.keys(sectionErrors).length > 0) {
            allErrors[section] = sectionErrors;
        }
        setFormErrors(allErrors);
        if (Object.keys(allErrors).length === 0) {
            e.preventDefault();
            handleSubmit(section, e)
        }
    };
    function nomineeTable(nominees) {
        return nominees?.reduce((acc, curr) => acc + Number(curr.nominee_percentage), 0);
    }

    // this function handle on save
    const handleSubmit = async (section, e) => {
        if ((section === "Part A (EPF)" || section === "Gratuity Nomination") && nomineeTable([...rows[section], formValues[section]]) > 100) {
            showInfo("info", "Maximum 100% allowed.")
            return
        }
        try {
            const paylaod = buildPayloadBySchema(fieldData[section]?.payload, formValues[section])

            const res = await services?.getInvokeRemote("POST", Urls(fieldData[section]?.apiEndpoint), paylaod);
            if (!res?.body?.error_status) {
                handleAPI !== undefined ? handleAPI(getDataKey) : callAPI(getDataKey)
                showSuccess("success", res?.body?.message);
                handleReset(section, e);
            } else {
                showError("error", res?.body?.errorMessage);
            }
        } catch (error) {
            console.error("error", error)
        }
    }

    // this function handle the reset form data
    const handleReset = (section, e) => {
        if (e) e.preventDefault();
        setFormValues(prev => ({
            ...prev,
            [section]: Object.fromEntries(
                fieldData[section].inputs?.map(f => [f.name, f.type === "autocomplete" ? null : ""])
            )
        }));
        setEditIndex(prev => ({ ...prev, [section]: null }));
    };


    // this function handle the delete table data
    const handleDelete = async (section, row, rowIndex) => {
        try {
            const payload = {
                "attribute_item_delete_key": fieldData[section]["deleteKey"],
                "education_id": row?.education_id || 0,
                "family_id": row?.family_id || 0,
                "language_id": row?.language_id || 0,
                "reference_id": row?.reference_id || 0,
                "experience_id": row?.experience_id || 0,
                "insurance_nomination_id": row?.insurance_nomination_id || 0,
                "efp_nomination_id": row?.efp_nomination_id || 0,
                "eps_nomination_id": row?.eps_nomination_id || 0,
                "esic_nominee_id": row?.esic_id || 0,
                "esic_witness_id": row?.esic_id || 0,
                "esic_family_insured_id": row?.esic_family_insured_id || 0,
                "esic_nominee_details_id": row?.esic_id || 0

            }
            const res = await services.getInvokeRemote("POST", Urls("DELETEEMPLOYEEDETAILSREMOVE"), payload)
            if (!res?.body?.error_status) {
                setRows(prev => {
                    const newRows = prev[section].filter((_, idx) => idx !== rowIndex);
                    return { ...prev, [section]: newRows };
                });
                if (editIndex[section] === rowIndex) {
                    handleReset(section);
                }
                showSuccess("success", res?.body?.message);
                setShowPopup({ visibel: false, section: "", idx: "", row: "" })
            } else {
                showError("error", res?.body?.errorMessage);
            }
        } catch (error) {
            console.error(error)
        }

    };

    const handleEdit = (section, idx) => {
        setEditIndex(prev => ({ ...prev, [section]: idx }));
        const originalData = rows[section][idx];
        const updatedData = { ...originalData };
        Object.keys(originalData).forEach(async key => {
            if (key.endsWith('file_id')) {
                const baseKey = key.slice(0, -3);
                updatedData[baseKey] = originalData[key];
            }
        });
        setFormValues(prev => ({
            ...prev,
            [section]: updatedData,
        }));
    };

    useEffect(() => {
        Object.entries(fieldData || {}).forEach(([sectionKey, sectionFields]) => {
            sectionFields.inputs?.forEach((field) => {
                if (field.type === "autocomplete" || field?.type === "select" && field?.optionType) {
                    const group = _.find(data?.attributeGroup, { attribute_group_key: field?.optionType });
                    if (group) {
                        const options = _.filter(data?.attributeItems, { attribute_group_id: group.attribute_group_id })
                            .map(item => ({
                                label: item.item_name,
                                value: field?.type === "select" ? item.attribute_item_id : item.item_name
                            }));
                        field.option = options;
                    } else {
                        field.option = [];
                    }
                }
            });
        });
    }, [fieldData, data?.attributeGroup, data?.attributeItems]);

    const callAPI = (key) => {
        data?.employee?.user_code && getEmployeeDetails(key, data?.employee?.user_code).then((res) => {
            if (key === "user_family_details") {
                const data = res?.user_family_details?.map(item => ({
                    label: item.member_name,
                    value: item?.family_id
                }));
                setFamilyData(data)

            } else {
                Object.entries(fieldData).forEach(([sectionName, sectionObj]) => {
                    setRows((prev) => ({ ...prev, [sectionName]: res?.[sectionObj.key] }))
                })
            }
        }).catch((error) => {
            console.error(error)
        })
    }

    useEffect(() => {
        if (getDataKey !== "employee_pf_nomination_declaration" && getDataKey !== "employee_gratuity_details" && getDataKey !== "employee_pf_details" && getDataKey !== "employee_esic_details") {
            callAPI(getDataKey)
        }

    }, [data?.employee, formJson?.[label]]);

    useEffect(() => {
        if (familyList?.length || getDataKey === "employee_pf_nomination_declaration" || getDataKey === "employee_esic_details" || getDataKey === "employee_gratuity_details") {
            setFamilyData(familyList)
        } else if (getDataKey === "employee_medical_insurance_nomination") {
            callAPI("user_family_details")
        }
    }, [familyList])

    useEffect(() => {
        row && setRows(row)
    }, [row])

    // Helper to create a new fieldData object with updated options for fields with is_API
    const getFieldDataWithOptions = (fieldData, rows, familyData) => {
        if (!fieldData) return {};
        return Object.fromEntries(
            Object.entries(fieldData).map(([sectionKey, section]) => {
                const updatedInputs = section.inputs.map(field => {
                    if (field.is_API && field.name === "family_id" || field.name === "eps_family_id") {
                        const usedIds = rows[sectionKey]?.map(r => r?.family_id || r?.eps1_family_id || r?.eps2_family_id) || [];
                        return {
                            ...field,
                            option: familyData?.filter(opt => !usedIds.includes(opt.value)) || [],
                        };
                    }
                    return field;
                });

                return [sectionKey, { ...section, inputs: updatedInputs }];
            })
        );
    };


    const exportToExcel = () => {
        const hasData = Object.entries(updatedFieldData).some(
            ([section]) => rows[section]?.length > 0
        );
        if (!hasData) {
            showInfo("Info", "There is no data to download");
            return;
        }
        const wb = XLSX.utils.book_new();
        Object.entries(updatedFieldData).forEach(([section, fields]) => {
            if (rows[section]?.length > 0) {
                const header = fields.inputs.filter(f => !f.fieldHide).map(f => f.label);
                const dataRows = rows[section].map(row => {
                    return fields.inputs.filter(f => !f.fieldHide).map(field => {
                        let cellValue = row[field.tableField] || row[field.name];
                        if (field.name === 'date_of_birth') return dateConvert(cellValue, 'day-mon-year');
                        return cellValue;
                    });
                });
                const emptyRow = new Array(header.length).fill('');
                const sheetData = [header, emptyRow, ...dataRows];
                const ws = XLSX.utils.aoa_to_sheet(sheetData);
                XLSX.utils.book_append_sheet(wb, ws, section);
            }
        });
        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([wbout], { type: 'application/octet-stream' });
        saveAs(blob, "medical_insurance_nomination.xlsx");
    };

    return useMemo(
        () => ({
            handleDelete, handleSave, handleChange, handleReset, handleEdit, setFormValues, setEditIndex, rows, fieldData, formValues, editIndex, formErrors, getFieldDataWithOptions, familyData, updatedFieldData, setUpdatedFieldData, exportToExcel, showPopup, setShowPopup
        }), [handleDelete, handleSave, handleChange, handleReset, handleEdit, setFormValues, setEditIndex, rows, fieldData, formValues, editIndex, formErrors, getFieldDataWithOptions, familyData, updatedFieldData, setUpdatedFieldData, exportToExcel, showPopup, setShowPopup])
};
